import { Badge, MoneyText } from '@paguz/ui';
import { Link } from '@tanstack/react-router';
import {
  CheckCircle2Icon,
  CircleAlertIcon,
  HelpCircleIcon,
  ScaleIcon,
  SparklesIcon,
  XCircleIcon,
} from 'lucide-react';
import type { AdminApplicationDetail } from '../../api/origination-schemas.ts';
import { DataList, Panel } from '../../components/page.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { KNOCK_OUT, RECOMMENDATION, REVIEW_ALERT } from '../../lib/labels.ts';
import { ROUTE_LABEL } from './labels.ts';

/** Salida del motor de decisión, ruta de la política y memo del Copiloto del Analista. */
export function RecommendationTab({ detail }: { detail: AdminApplicationDetail }) {
  const rec = detail.recommendation;
  const route = detail.policyRoute;
  const memo = detail.copilotMemo;
  if (!rec)
    return (
      <Panel>
        <p className="text-sm text-muted-foreground">
          El motor de decisión aún no evalúa este expediente.
        </p>
      </Panel>
    );
  return (
    <div className="flex flex-col gap-6">
      {route && (
        <Panel
          title={
            <span className="inline-flex items-center gap-2">
              <ScaleIcon className="size-5 text-highlight" /> ¿Por qué{' '}
              {route.route === 'ANALYST' ? 'llega al analista' : 'se decidió solo'}?
            </span>
          }
          description={`Política de decisión v${route.policyVersion} · ruta: ${ROUTE_LABEL[route.route]}`}
          actions={
            <Link
              to="/configuracion/$section"
              params={{ section: 'decision' }}
              className="text-sm font-semibold text-primary hover:underline"
            >
              Ver política de decisión
            </Link>
          }
        >
          <ul className="flex flex-col gap-1.5 text-sm">
            {route.why.map((w) => (
              <li key={w} className="flex gap-2">
                <CircleAlertIcon className="mt-0.5 size-4 shrink-0 text-highlight" /> {w}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel
        title="Recomendación del motor"
        description={`${rec.engineVersion} · ${formatDateTime(rec.createdAt)} · código determinístico (la IA no decide)`}
      >
        <div className="flex flex-col gap-5">
          <DataList
            columns={4}
            items={[
              {
                label: 'Recomendación',
                value: (
                  <Badge variant={RECOMMENDATION[rec.recommendation].tone} className="text-sm">
                    {RECOMMENDATION[rec.recommendation].label}
                  </Badge>
                ),
              },
              {
                label: 'Puntaje',
                value: <span className="text-lg font-semibold">{rec.score} / 1000</span>,
              },
              {
                label: 'Cupo recomendado',
                value: rec.recommendedLimit ? (
                  <MoneyText
                    value={rec.recommendedLimit.amount}
                    size="md"
                    className="font-semibold"
                  />
                ) : (
                  '—'
                ),
              },
              {
                label: 'Decidible por política',
                value: rec.autoDecidable ? 'Sí' : 'No, requiere analista',
              },
            ]}
          />
          <div>
            <p className="mb-1.5 text-xs font-semibold text-muted-foreground">Motivos</p>
            <ul className="flex flex-col gap-1 text-sm">
              {rec.reasons.map((r) => (
                <li key={r} className="flex gap-2">
                  <CheckCircle2Icon className="mt-0.5 size-4 shrink-0 text-success" /> {r}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-1.5 text-xs font-semibold text-muted-foreground">Reglas evaluadas</p>
            <ul className="grid gap-2 md:grid-cols-2">
              {rec.rulesFired.map((r) => (
                <li
                  key={r.rule}
                  className="flex gap-2 rounded-xl border border-border px-3 py-2 text-sm"
                >
                  {r.outcome === 'PASS' ? (
                    <CheckCircle2Icon className="mt-0.5 size-4 shrink-0 text-success" />
                  ) : (
                    <XCircleIcon className="mt-0.5 size-4 shrink-0 text-destructive" />
                  )}
                  <span>
                    <code className="font-mono text-xs">{r.rule}</code>
                    {r.detail && <span className="block text-muted-foreground">{r.detail}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {(rec.knockOuts.length > 0 || rec.alerts.length > 0) && (
            <div className="flex flex-wrap gap-1.5">
              {rec.knockOuts.map((k) => (
                <Badge key={k} variant="danger">
                  {KNOCK_OUT[k]}
                </Badge>
              ))}
              {rec.alerts.map((a) => (
                <Badge key={a} variant="warning">
                  {REVIEW_ALERT[a]}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </Panel>

      <Panel
        title={
          <span className="inline-flex items-center gap-2">
            <SparklesIcon className="size-5 text-highlight" /> Memo del Copiloto del Analista
          </span>
        }
        description={
          memo
            ? `${memo.model} · ${formatDateTime(memo.generatedAt)} · borrador para tu criterio; cita sus fuentes`
            : undefined
        }
      >
        {memo ? (
          <div className="flex flex-col gap-4 text-sm">
            <p className="leading-relaxed">{memo.summary}</p>
            <div className="grid gap-4 md:grid-cols-2">
              <MemoList
                title="Fortalezas"
                items={memo.strengths}
                icon={<CheckCircle2Icon className="mt-0.5 size-4 shrink-0 text-success" />}
              />
              <MemoList
                title="Riesgos"
                items={memo.risks}
                icon={<CircleAlertIcon className="mt-0.5 size-4 shrink-0 text-warning" />}
              />
            </div>
            <MemoList
              title="Preguntas sugeridas para el cliente"
              items={memo.questions}
              icon={<HelpCircleIcon className="mt-0.5 size-4 shrink-0 text-highlight" />}
            />
            <div>
              <p className="mb-1 text-xs font-semibold text-muted-foreground">Fuentes citadas</p>
              <ul className="flex flex-wrap gap-1.5">
                {memo.citations.map((c) => (
                  <li key={c.ref}>
                    <Badge variant="outline" title={c.ref}>
                      {c.label}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            El copiloto no ha generado un memo para este expediente.
          </p>
        )}
      </Panel>
    </div>
  );
}

function MemoList({
  title,
  items,
  icon,
}: {
  title: string;
  items: readonly string[];
  icon: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-muted-foreground">{title}</p>
      <ul className="flex flex-col gap-1">
        {items.map((i) => (
          <li key={i} className="flex gap-2">
            {icon} {i}
          </li>
        ))}
      </ul>
    </div>
  );
}
